import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, X,
  Loader2, Music, Star, Zap, Upload, Link, Trash2, Download,
  Filter, Building, CheckSquare, FileText, ExternalLink
} from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { formatDateToLocal } from '../lib/dateUtils';

export default function EventCalendar({ api, userType = 'admin' }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [dayDetailDate, setDayDetailDate] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Stats
  const [stats, setStats] = useState({
    total_events: 0,
    this_month: 0,
    upcoming: 0,
    with_spike: 0
  });
  
  // Adapts management (Admin only)
  const [adapts, setAdapts] = useState([]);
  const [showAdaptForm, setShowAdaptForm] = useState(false);
  const [newAdapt, setNewAdapt] = useState({ platform: '', size: '', type: 'Static' });
  
  // Filters
  const [clients, setClients] = useState([]);
  const [selectedClientFilter, setSelectedClientFilter] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  
  // Event Modal State
  const [eventForm, setEventForm] = useState({
    client_id: '',
    event_name: '',
    event_date: '',
    event_time: '',
    artist_name: '',
    artist_press_kit: [],
    artist_press_kit_url: '',
    genre: '',
    spike: 'No',
    references: [],
    reference_url: '',
    notes: '',
    adapts: []
  });
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [filteredClients, setFilteredClients] = useState([]);
  const [showClientSearchDropdown, setShowClientSearchDropdown] = useState(false);
  const [selectedAdapts, setSelectedAdapts] = useState([]);
  const [customAdaptEntry, setCustomAdaptEntry] = useState({ platform: '', size: '', type: 'Static', due_date: '' });
  const [uploading, setUploading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [savingEvent, setSavingEvent] = useState(false);
  
  // Multiselect for bulk delete
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  
  const isAdminPortal = userType === 'admin';
  const isEmployeePortal = userType === 'employee';
  const isClientPortal = userType === 'client';
  
  const getApiEndpoint = useCallback(() => {
    // Legacy endpoints (used for adapts and other non-migrated features)
    if (isAdminPortal) return '/api/admin';
    if (isEmployeePortal) return '/api/employee';
    return '/api/client';
  }, [isAdminPortal, isEmployeePortal]);
  
  // Fetch events - Now uses V1 API
  const fetchEvents = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      
      // Calculate start/end dates for the month
      const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      
      let params = { 
        start_date: startDate,
        end_date: endDate,
        page: 1,
        page_size: 200 
      };
      
      if (!isClientPortal && selectedClientFilter) {
        params.client_id = selectedClientFilter;
      }
      
      // Use V1 API for events
      const response = await api.get('/api/v1/events', { params });
      // V1 returns { items: [...], total, ... }
      setEvents(response.data?.items || response.data || []);
    } catch (error) {
      console.error('V1 events error, falling back to legacy:', error);
      // Fallback to legacy API
      try {
        const month = currentDate.getMonth() + 1;
        const year = currentDate.getFullYear();
        let params = { month: month.toString(), year: year.toString() };
        if (!isClientPortal && selectedClientFilter) {
          params.client_id = selectedClientFilter;
        }
        const fallback = await api.get(`${getApiEndpoint()}/events`, { params });
        setEvents(fallback.data || []);
      } catch (fallbackErr) {
        console.error('Fallback also failed:', fallbackErr);
      }
    } finally {
      setLoading(false);
    }
  }, [api, getApiEndpoint, currentDate, selectedClientFilter, isClientPortal]);
  
  // Fetch stats - Now uses V1 API
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/events/stats');
      setStats(response.data || {});
    } catch (error) {
      console.error('V1 stats error, falling back to legacy:', error);
      // Fallback to legacy
      try {
        const fallback = await api.get(`${getApiEndpoint()}/events/stats`);
        setStats(fallback.data || {});
      } catch (fallbackErr) {
        console.error('Fallback also failed:', fallbackErr);
      }
    }
  }, [api, getApiEndpoint]);
  
  // Fetch adapts
  const fetchAdapts = useCallback(async () => {
    try {
      const response = await api.get(`${getApiEndpoint()}/adapts`);
      setAdapts(response.data || []);
    } catch (error) {
      console.error('Error fetching adapts:', error);
    }
  }, [api, getApiEndpoint]);
  
  // Fetch clients for filter
  const fetchClients = useCallback(async () => {
    if (isClientPortal) return;
    try {
      const endpoint = isAdminPortal ? '/api/clients' : '/api/employee/clients';
      const response = await api.get(endpoint);
      setClients(response.data || []);
      setFilteredClients(response.data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  }, [api, isAdminPortal, isClientPortal]);
  
  useEffect(() => {
    fetchEvents();
    fetchStats();
    fetchAdapts();
    fetchClients();
  }, [fetchEvents, fetchStats, fetchAdapts, fetchClients]);
  
  // Calendar generation
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Previous month days - set to noon to avoid timezone issues
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDay - 1; i >= 0; i--) {
      const d = new Date(year, month - 1, prevMonthLastDay - i, 12, 0, 0);
      days.push({
        date: d,
        isCurrentMonth: false
      });
    }
    
    // Current month days - set to noon to avoid timezone issues
    for (let i = 1; i <= daysInMonth; i++) {
      const d = new Date(year, month, i, 12, 0, 0);
      days.push({
        date: d,
        isCurrentMonth: true
      });
    }
    
    // Next month days - set to noon to avoid timezone issues
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const d = new Date(year, month + 1, i, 12, 0, 0);
      days.push({
        date: d,
        isCurrentMonth: false
      });
    }
    
    return days;
  };
  
  const days = getDaysInMonth(currentDate);
  const today = new Date();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  const getEventsForDate = (date) => {
    const dateStr = formatDateToLocal(date);
    return events.filter(e => e.event_date === dateStr);
  };
  
  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };
  
  // Calculate due date based on spike status
  // Non-spike: 7 days before event date
  // Spike: 40 days before event date
  const calculateDueDate = (eventDate, isSpike) => {
    if (!eventDate) return '';
    const date = new Date(eventDate + 'T00:00:00');
    const daysBeforeEvent = isSpike === 'Yes' ? 40 : 7;
    date.setDate(date.getDate() - daysBeforeEvent);
    return formatDateToLocal(date);
  };

  const handleDayClick = (day) => {
    // If day has events, open preview; otherwise open create event
    const dayEvents = getEventsForDate(day.date);
    if (dayEvents.length > 0) {
      setDayDetailDate(day.date);
      return;
    }
    setSelectedDate(day.date);
    setSelectedEvent(null);
    setIsEditMode(false);
    setEventForm({
      client_id: '',
      event_name: '',
      event_date: formatDateToLocal(day.date),
      event_time: '',
      artist_name: '',
      artist_press_kit: [],
      artist_press_kit_url: '',
      genre: '',
      spike: 'No',
      references: [],
      reference_url: '',
      notes: '',
      adapts: []
    });
    setSelectedClient(null);
    setClientSearch('');
    setSelectedAdapts([]);
    setShowEventModal(true);
  };
  
  const handleEventClick = (e, event) => {
    e.stopPropagation();
    setSelectedEvent(event);
    setIsEditMode(true);  // Enable edit mode for existing events
    setEventForm({
      client_id: event.client_id,
      event_name: event.event_name,
      event_date: event.event_date,
      event_time: event.event_time || '',
      artist_name: event.artist_name,
      artist_press_kit: event.artist_press_kit || [],
      artist_press_kit_url: event.artist_press_kit_url || '',
      genre: event.genre,
      spike: event.spike,
      references: event.references || [],
      reference_url: event.reference_url || '',
      notes: event.notes || '',
      adapts: event.adapts || []
    });
    setSelectedClient({ _id: event.client_id, name: event.client_name });
    setClientSearch(event.client_name);
    setSelectedAdapts(event.adapts || []);
    setShowEventModal(true);
  };
  
  // Client search
  useEffect(() => {
    if (clientSearch) {
      const filtered = clients.filter(c => 
        c.name?.toLowerCase().includes(clientSearch.toLowerCase())
      );
      setFilteredClients(filtered);
    } else {
      setFilteredClients(clients);
    }
  }, [clientSearch, clients]);
  
  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setEventForm(prev => ({ ...prev, client_id: client._id }));
    setClientSearch(client.name);
    setShowClientSearchDropdown(false);
  };
  
  // Toggle adapt selection - now calculates due date based on spike status
  const toggleAdaptSelection = (adapt) => {
    const existing = selectedAdapts.find(a => a.adapt_id === adapt._id);
    if (existing) {
      setSelectedAdapts(prev => prev.filter(a => a.adapt_id !== adapt._id));
    } else {
      // Calculate due date: 40 days before for spike, 7 days before for non-spike
      const calculatedDueDate = calculateDueDate(eventForm.event_date, eventForm.spike);
      setSelectedAdapts(prev => [...prev, {
        adapt_id: adapt._id,
        platform: adapt.platform,
        size: adapt.size,
        type: adapt.type,
        due_date: calculatedDueDate || eventForm.event_date,
        is_custom: false
      }]);
    }
  };
  
  // Update adapt due date
  const updateAdaptDueDate = (adaptId, dueDate) => {
    setSelectedAdapts(prev => prev.map(a => 
      (a.adapt_id === adaptId || a.temp_id === adaptId) ? { ...a, due_date: dueDate } : a
    ));
  };
  
  // Add custom adapt - uses calculated due date based on spike status
  const addCustomAdapt = () => {
    if (!customAdaptEntry.platform || !customAdaptEntry.size) {
      toast.error('Please fill platform and size');
      return;
    }
    const calculatedDueDate = calculateDueDate(eventForm.event_date, eventForm.spike);
    setSelectedAdapts(prev => [...prev, {
      temp_id: `custom_${Date.now()}`,
      platform: customAdaptEntry.platform,
      size: customAdaptEntry.size,
      type: customAdaptEntry.type,
      due_date: customAdaptEntry.due_date || calculatedDueDate || eventForm.event_date,
      is_custom: true
    }]);
    setCustomAdaptEntry({ platform: '', size: '', type: 'Static', due_date: '' });
  };
  
  // Remove adapt
  const removeAdapt = (id) => {
    setSelectedAdapts(prev => prev.filter(a => (a.adapt_id || a.temp_id) !== id));
  };
  
  // File upload handler
  const handleFileUpload = async (e, field) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    try {
      const uploadedUrls = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post('/api/files/s3-upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (response.data?.url) {
          uploadedUrls.push(response.data.url);
        }
      }
      setEventForm(prev => ({
        ...prev,
        [field]: [...prev[field], ...uploadedUrls]
      }));
      toast.success('Files uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };
  
  // Add URL
  const addUrl = (field, url) => {
    if (!url) return;
    setEventForm(prev => ({
      ...prev,
      [field]: [...prev[field], url]
    }));
  };
  
  // Remove file/url
  const removeFile = (field, index) => {
    setEventForm(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };
  
  // Submit event (create or update)
  const handleSubmitEvent = async (e) => {
    e.preventDefault();
    
    if (!eventForm.client_id || !eventForm.event_name || !eventForm.artist_name || !eventForm.genre) {
      toast.error('Please fill all required fields');
      return;
    }
    
    if (selectedAdapts.length === 0) {
      toast.error('Please select at least one adapt');
      return;
    }
    
    // Check all adapts have due dates
    const missingDates = selectedAdapts.filter(a => !a.due_date);
    if (missingDates.length > 0) {
      toast.error('Please set due date for all adapts');
      return;
    }
    
    setSavingEvent(true);
    try {
      const eventData = {
        ...eventForm,
        adapts: selectedAdapts
      };
      
      if (selectedEvent && isEditMode) {
        // Update existing event - Use V1 API
        try {
          await api.put(`/api/v1/events/${selectedEvent._id}`, eventData);
        } catch (v1Error) {
          console.error('V1 update failed, trying legacy:', v1Error);
          await api.put(`${getApiEndpoint()}/events/${selectedEvent._id}`, eventData);
        }
        toast.success('Event updated successfully!');
      } else {
        // Create new event - Use V1 API
        try {
          await api.post('/api/v1/events', eventData);
        } catch (v1Error) {
          console.error('V1 create failed, trying legacy:', v1Error);
          await api.post(`${getApiEndpoint()}/events`, eventData);
        }
        toast.success('Event created successfully! Tasks have been auto-created for each adapt.');
      }
      
      setShowEventModal(false);
      setIsEditMode(false);
      if (selectedEvent && isEditMode) {
        // Optimistic update for edits
        setEvents(prev => prev.map(e => e._id === selectedEvent._id ? { ...e, ...eventData } : e));
      }
      // Silent refetch for both create (need server ID) and update (sync computed fields)
      fetchEvents({ silent: true });
      fetchStats();
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error(selectedEvent ? 'Failed to update event' : 'Failed to create event');
    } finally {
      setSavingEvent(false);
    }
  };
  
  // Download events as CSV
  const handleDownloadEventsCSV = async () => {
    try {
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      
      // Calculate date range for V1 API
      const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      
      // Try V1 API first
      try {
        const response = await api.get('/api/v1/events/export-csv', {
          params: { start_date: startDate, end_date: endDate },
          responseType: 'blob'
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `events_${year}_${month.toString().padStart(2, '0')}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Events exported successfully');
        return;
      } catch (v1Error) {
        console.error('V1 export failed, trying legacy:', v1Error);
      }
      
      // Fallback to legacy API
      const response = await api.get(`${getApiEndpoint()}/events/export-csv`, {
        params: { month: month.toString(), year: year.toString() },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `events_${year}_${month.toString().padStart(2, '0')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Events exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export events');
    }
  };
  
  // Toggle event selection for bulk delete
  const toggleEventSelection = (eventId) => {
    setSelectedEventIds(prev => {
      if (prev.includes(eventId)) {
        return prev.filter(id => id !== eventId);
      } else {
        return [...prev, eventId];
      }
    });
  };
  
  // Select/Deselect all events in current month
  const toggleSelectAll = () => {
    const monthEvents = getEventsForMonth();
    if (selectedEventIds.length === monthEvents.length) {
      setSelectedEventIds([]);
    } else {
      setSelectedEventIds(monthEvents.map(e => e._id));
    }
  };
  
  // Bulk delete events
  const handleBulkDelete = async () => {
    if (selectedEventIds.length === 0) return;
    // Optimistic removal
    const previousEvents = events;
    const deletingIds = [...selectedEventIds];
    setEvents(prev => prev.filter(e => !deletingIds.includes(e._id)));
    setSelectedEventIds([]);
    setShowBulkDeleteConfirm(false);
    
    try {
      try {
        await api.post('/api/v1/events/bulk-delete', { event_ids: deletingIds });
      } catch (v1Error) {
        console.error('V1 bulk delete failed, trying legacy:', v1Error);
        await api.post(`${getApiEndpoint()}/events/bulk-delete`, { event_ids: deletingIds });
      }
      toast.success(`Deleted ${deletingIds.length} events successfully`);
      fetchStats();
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Failed to delete events');
      setEvents(previousEvents); // Rollback
    }
  };
  
  // CSV Upload state
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  
  // Handle CSV file upload
  const handleCSVUpload = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }

    // Show alert about Instagram Story default adapt
    const confirmUpload = window.confirm(
      'Important: Each event uploaded via CSV will have "Instagram Story" adapt added by default.\n\n' +
      'Due dates will be calculated automatically:\n' +
      '• Spike events: 40 days before event date\n' +
      '• Non-spike events: 7 days before event date\n\n' +
      'You must manually add other adapts (Posts, Reels, etc.) in the Event Calendar section for each event after upload.\n\n' +
      'Do you want to proceed?'
    );

    if (!confirmUpload) {
      return;
    }

    setCsvUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);

      const response = await api.post(`${getApiEndpoint()}/events/upload-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.created_count > 0) {
        toast.success(response.data.message);
        // Remind user about adding more adapts
        setTimeout(() => {
          toast.info('Remember: Only Instagram Story adapt was added. Add other adapts manually for each event.', {
            duration: 8000
          });
        }, 2000);
      }

      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }

      setShowCSVModal(false);
      setCsvFile(null);
      fetchEvents({ silent: true });
      fetchStats();
    } catch (error) {
      console.error('CSV upload error:', error);
      toast.error('Failed to upload CSV');
    } finally {
      setCsvUploading(false);
    }
  };
  
  // Download sample CSV
  const handleDownloadSampleCSV = async () => {
    try {
      const response = await api.get(`${getApiEndpoint()}/events/sample-csv`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'events_sample.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download sample CSV');
    }
  };
  
  // Delete event
  const handleDeleteEvent = async () => {
    if (!selectedEvent?._id) return;
    if (!window.confirm('Are you sure you want to delete this event and all its associated tasks?')) return;
    
    // Optimistic removal
    const previousEvents = events;
    const deletingId = selectedEvent._id;
    setEvents(prev => prev.filter(e => e._id !== deletingId));
    setShowEventModal(false);
    
    try {
      try {
        await api.delete(`/api/v1/events/${deletingId}`);
      } catch (v1Error) {
        console.error('V1 delete failed, trying legacy:', v1Error);
        await api.delete(`${getApiEndpoint()}/events/${deletingId}`);
      }
      toast.success('Event deleted successfully');
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete event');
      setEvents(previousEvents); // Rollback
    }
  };
  
  // Create adapt (Admin only)
  const handleCreateAdapt = async () => {
    if (!newAdapt.platform || !newAdapt.size) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      await api.post('/api/admin/adapts', newAdapt);
      toast.success('Adapt created successfully');
      setNewAdapt({ platform: '', size: '', type: 'Static' });
      setShowAdaptForm(false);
      fetchAdapts();
    } catch (error) {
      toast.error('Failed to create adapt');
    }
  };
  
  // Delete adapt (Admin only)
  const handleDeleteAdapt = async (adaptId) => {
    if (!window.confirm('Delete this adapt?')) return;
    try {
      await api.delete(`/api/admin/adapts/${adaptId}`);
      toast.success('Adapt deleted');
      fetchAdapts();
    } catch (error) {
      toast.error('Failed to delete adapt');
    }
  };
  
  // Get events for current month (for table view)
  const getEventsForMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    return events.filter(event => {
      const eventDate = new Date(event.event_date + 'T00:00:00');
      return eventDate.getFullYear() === year && eventDate.getMonth() === month;
    });
  };
  
  return (
    <div className="space-y-6">
      {/* Stats Section */}
      <div className="grid grid-cols-4 gap-4">
        <div className="glass-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <CalendarIcon className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1a1a2e]">{stats.total_events}</p>
              <p className="text-sm text-neutral-500">Total Events</p>
            </div>
          </div>
        </div>
        <div className="glass-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Music className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1a1a2e]">{stats.this_month}</p>
              <p className="text-sm text-neutral-500">This Month</p>
            </div>
          </div>
        </div>
        <div className="glass-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
              <Star className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1a1a2e]">{stats.upcoming}</p>
              <p className="text-sm text-neutral-500">Upcoming</p>
            </div>
          </div>
        </div>
        <div className="glass-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
              <Zap className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1a1a2e]">{stats.with_spike}</p>
              <p className="text-sm text-neutral-500">With Spike</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Filters (Admin/Employee only) */}
      {!isClientPortal && (
        <div className="glass-card p-4 relative z-30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Filter className="w-5 h-5 text-neutral-500" />
              <div className="relative">
                <button
                  onClick={() => setShowClientDropdown(!showClientDropdown)}
                  className="flex items-center gap-2 px-4 py-2 border border-white/40 rounded-xl hover:bg-white/60"
                >
                  <Building className="w-4 h-4 text-neutral-500" />
                  <span>{selectedClientFilter ? clients.find(c => c._id === selectedClientFilter)?.name : 'All Clients'}</span>
                </button>
                {showClientDropdown && (
                  <div className="absolute z-20 mt-2 w-64 glass-card rounded-xl shadow-lg max-h-64 overflow-y-auto">
                    <button
                      onClick={() => { setSelectedClientFilter(''); setShowClientDropdown(false); }}
                      className="w-full text-left px-4 py-2 hover:bg-white/60"
                    >
                      All Clients
                    </button>
                    {clients.map(client => (
                      <button
                        key={client._id}
                        onClick={() => { setSelectedClientFilter(client._id); setShowClientDropdown(false); }}
                        className={`w-full text-left px-4 py-2 hover:bg-white/60 ${selectedClientFilter === client._id ? 'bg-purple-50 text-purple-700' : ''}`}
                      >
                        {client.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {selectedClientFilter && (
                <button
                  onClick={() => setSelectedClientFilter('')}
                  className="px-3 py-1.5 text-sm text-neutral-600 hover:text-[#1a1a2e] border border-white/40 rounded-xl"
                >
                  Clear Filter
                </button>
              )}
            </div>
            
            {/* CSV Upload and Download Buttons (Admin, Employee, and Client) */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleDownloadEventsCSV}
                className="flex items-center gap-2 px-4 py-2 border border-purple-600 text-purple-600 rounded-xl hover:bg-purple-50 transition-colors"
                data-testid="download-csv-btn"
              >
                <Download className="w-4 h-4" />
                Download CSV
              </button>
              <button
                onClick={() => setShowCSVModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors"
                data-testid="upload-csv-btn"
              >
                <Upload className="w-4 h-4" />
                Upload CSV
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Calendar */}
      <div className="soft-card overflow-hidden">
        {/* Calendar Header */}
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-2 hover:bg-white/20 rounded-xl transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
              <h2 className="text-xl font-bold text-white">
                {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h2>
              <button
                onClick={() => navigateMonth(1)}
                className="p-2 hover:bg-white/20 rounded-xl transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-white" />
              </button>
            </div>
            <button
              onClick={() => setCurrentDate(new Date())}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-xl text-sm font-medium transition-colors"
            >
              Today
            </button>
          </div>
        </div>
        
        {/* Week Days */}
        <div className="grid grid-cols-7 bg-white/40 border-b border-white/40">
          {weekDays.map(day => (
            <div key={day} className="px-4 py-3 text-center text-sm font-semibold text-neutral-600">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar Grid */}
        {loading ? (
          <div className="h-96 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : (
          <div className="grid grid-cols-7">
            {days.map((day, index) => {
              const dayEvents = getEventsForDate(day.date);
              const isToday = day.date.toDateString() === today.toDateString();
              
              return (
                <div
                  key={index}
                  onClick={() => !isClientPortal && handleDayClick(day)}
                  className={`min-h-[120px] p-2 border-b border-r border-white/30 transition-colors ${
                    day.isCurrentMonth ? 'bg-white' : 'bg-white/40'
                  } ${!isClientPortal ? 'cursor-pointer hover:bg-white/60' : ''}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className={`text-sm font-medium ${
                      isToday 
                        ? 'w-7 h-7 rounded-full bg-purple-500 text-white flex items-center justify-center'
                        : day.isCurrentMonth ? 'text-[#1a1a2e]' : 'text-neutral-400'
                    }`}>
                      {day.date.getDate()}
                    </div>
                    {dayEvents.length > 0 && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-600" data-testid={`event-count-${formatDateToLocal(day.date)}`}>
                        {dayEvents.length}
                      </span>
                    )}
                  </div>
                  
                  <div className="space-y-1">
                    {dayEvents.slice(0, 3).map((event, eventIndex) => (
                      <div
                        key={eventIndex}
                        onClick={(e) => handleEventClick(e, event)}
                        className="text-xs px-2 py-1 rounded-xl bg-purple-100 text-purple-700 truncate hover:bg-purple-200 cursor-pointer flex items-center gap-1"
                        title={event.event_name}
                      >
                        <Music className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{event.event_name}</span>
                        {event.spike === 'Yes' && <Zap className="w-3 h-3 text-orange-500 flex-shrink-0" />}
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <button 
                        onClick={(e) => { e.stopPropagation(); setDayDetailDate(day.date); }}
                        className="text-xs text-purple-600 font-medium hover:underline cursor-pointer"
                        data-testid={`event-day-more-${formatDateToLocal(day.date)}`}
                      >
                        +{dayEvents.length - 3} more
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Events Table View */}
      <div className="soft-card overflow-hidden">
        <div className="p-4 border-b border-white/40 bg-purple-50 dark:bg-transparent">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[#1a1a2e] flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              Events Table
              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                {getEventsForMonth().length} events
              </span>
            </h3>
            {/* Bulk Delete Button */}
            {userType === 'admin' && selectedEventIds.length > 0 && (
              <button
                onClick={() => setShowBulkDeleteConfirm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors"
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="w-4 h-4" />
                Delete Selected ({selectedEventIds.length})
              </button>
            )}
          </div>
        </div>
        
        {getEventsForMonth().length === 0 ? (
          <div className="text-center py-12 text-neutral-500">
            <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
            <p>No events for {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="events-table">
              <thead className="bg-white/40 border-b border-white/40">
                <tr>
                  {userType === 'admin' && (
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedEventIds.length === getEventsForMonth().length && getEventsForMonth().length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 text-purple-600 rounded border-white/40"
                      />
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">Event Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">Artist</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">Genre</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">Spike</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">Adapts</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/40">
                {getEventsForMonth()
                  .sort((a, b) => new Date(a.event_date) - new Date(b.event_date))
                  .map((event) => {
                    const eventDate = new Date(event.event_date + 'T00:00:00');
                    const clientName = clients.find(c => c._id === event.client_id)?.name || 'Unknown';
                    const isSelected = selectedEventIds.includes(event._id);
                    
                    return (
                      <tr key={event._id} className={`hover:bg-white/60 transition-colors ${isSelected ? 'bg-purple-50' : ''}`}>
                        {userType === 'admin' && (
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleEventSelection(event._id)}
                              className="w-4 h-4 text-purple-600 rounded border-white/40"
                              disabled={userType !== 'admin' && event.can_delete === false}
                              title={userType !== 'admin' && event.can_delete === false ? 'No delete permission' : ''}
                            />
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-[#1a1a2e]">
                            {eventDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}
                          </div>
                          <div className="text-xs text-neutral-500">
                            {eventDate.toLocaleDateString('en-US', { weekday: 'short' })}
                            {event.event_time && ` • ${event.event_time}`}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-medium text-[#1a1a2e]">{event.event_name}</span>
                          {event.notes && (
                            <p className="text-xs text-neutral-500 truncate max-w-[200px]" title={event.notes}>{event.notes}</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-neutral-600">{clientName}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-neutral-600">{event.artist_name || '-'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-neutral-600">{event.genre || '-'}</span>
                        </td>
                        <td className="px-4 py-3">
                          {event.spike === 'Yes' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                              <Zap className="w-3 h-3" /> Yes
                            </span>
                          ) : (
                            <span className="text-xs text-neutral-400">No</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                            {event.adapts?.length || 0} adapts
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {(userType === 'admin' || event.can_edit !== false) && (
                            <button
                              onClick={(e) => handleEventClick(e, event)}
                              className="px-3 py-1.5 text-sm bg-purple-50 text-purple-700 rounded-xl hover:bg-purple-100 transition-colors"
                              data-testid={`edit-event-${event._id}`}
                            >
                              Edit
                            </button>
                          )}
                          {!event.can_edit && event.can_edit !== undefined && userType !== 'admin' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setSelectedEvent(event); setShowEventModal(true); setIsEditMode(false); }}
                              className="px-3 py-1.5 text-sm bg-white/40 text-neutral-600 rounded-xl hover:bg-neutral-100 transition-colors"
                              data-testid={`view-event-${event._id}`}
                            >
                              View
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Adapts Management Section (Admin Only) */}
      {isAdminPortal && (
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#1a1a2e] flex items-center gap-2">
              <CheckSquare className="w-5 h-5 text-purple-600" />
              Adapts
            </h3>
            <button
              onClick={() => setShowAdaptForm(!showAdaptForm)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-xl hover:bg-purple-600"
            >
              <Plus className="w-4 h-4" />
              Add Adapt
            </button>
          </div>
          
          {showAdaptForm && (
            <div className="mb-4 p-4 bg-white/40 rounded-xl border border-white/40">
              <div className="grid grid-cols-4 gap-4">
                <input
                  type="text"
                  placeholder="Platform (e.g., Instagram)"
                  value={newAdapt.platform}
                  onChange={(e) => setNewAdapt(prev => ({ ...prev, platform: e.target.value }))}
                  className="px-3 py-2 border border-white/40 rounded-xl"
                />
                <input
                  type="text"
                  placeholder="Size (e.g., 1080x1080)"
                  value={newAdapt.size}
                  onChange={(e) => setNewAdapt(prev => ({ ...prev, size: e.target.value }))}
                  className="px-3 py-2 border border-white/40 rounded-xl"
                />
                <select
                  value={newAdapt.type}
                  onChange={(e) => setNewAdapt(prev => ({ ...prev, type: e.target.value }))}
                  className="px-3 py-2 border border-white/40 rounded-xl"
                >
                  <option value="Static">Static</option>
                  <option value="Video">Video</option>
                </select>
                <button
                  onClick={handleCreateAdapt}
                  className="px-4 py-2 bg-green-500 text-white rounded-xl hover:bg-green-600"
                >
                  Create
                </button>
              </div>
            </div>
          )}
          
          {adapts.length === 0 ? (
            <p className="text-neutral-500 text-center py-4">No adapts created yet</p>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {adapts.map(adapt => (
                <div key={adapt._id} className="flex items-center justify-between p-3 bg-white/40 rounded-xl border border-white/40">
                  <div>
                    <p className="font-medium text-[#1a1a2e]">{adapt.platform}</p>
                    <p className="text-sm text-neutral-500">{adapt.size} • {adapt.type}</p>
                  </div>
                  <button
                    onClick={() => handleDeleteAdapt(adapt._id)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Event Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-[#1a1a1a] border-b border-white/40 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#1a1a2e]">
                {selectedEvent ? (isEditMode ? 'Edit Event' : 'Event Details') : 'Create Event'}
              </h2>
              <button onClick={() => { setShowEventModal(false); setIsEditMode(false); }} className="p-2 hover:bg-neutral-100 rounded-xl">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmitEvent} className="p-6 space-y-4">
              {/* Client Selection */}
              <div className="relative">
                <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                  Client Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={clientSearch}
                  onChange={(e) => { setClientSearch(e.target.value); setShowClientSearchDropdown(true); }}
                  onFocus={() => setShowClientSearchDropdown(true)}
                  placeholder="Search clients..."
                  className="w-full px-4 py-3 border border-white/40 rounded-xl"
                  required
                  disabled={!!selectedEvent}
                />
                {showClientSearchDropdown && filteredClients.length > 0 && !selectedEvent && (
                  <div className="absolute z-10 w-full mt-1 glass-card rounded-xl shadow-lg max-h-48 overflow-y-auto">
                    {filteredClients.map(client => (
                      <button
                        key={client._id}
                        type="button"
                        onClick={() => handleClientSelect(client)}
                        className="w-full text-left px-4 py-2 hover:bg-white/60"
                      >
                        {client.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Event Name & Date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                    Event Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={eventForm.event_name}
                    onChange={(e) => setEventForm(prev => ({ ...prev, event_name: e.target.value }))}
                    placeholder="Enter event name"
                    className="w-full px-4 py-3 border border-white/40 rounded-xl"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                    Event Date <span className="text-red-500">*</span>
                    <span className="ml-2 text-xs text-neutral-500 font-normal">(Locked)</span>
                  </label>
                  <input
                    type="date"
                    value={eventForm.event_date}
                    className="w-full px-4 py-3 border border-white/40 rounded-xl bg-neutral-100"
                    disabled
                  />
                </div>
              </div>
              
              {/* Event Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                    Event Time
                  </label>
                  <input
                    type="time"
                    value={eventForm.event_time || ''}
                    onChange={(e) => setEventForm(prev => ({ ...prev, event_time: e.target.value }))}
                    className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-purple-500"
                    placeholder="HH:MM"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                    Assignment Date <span className="text-xs text-neutral-500 font-normal">(Auto-set to today)</span>
                  </label>
                  <input
                    type="date"
                    value={formatDateToLocal(new Date())}
                    className="w-full px-4 py-3 border border-white/40 rounded-xl bg-neutral-100"
                    disabled
                  />
                </div>
              </div>
              
              {/* Artist & Genre */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                    Artist Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={eventForm.artist_name}
                    onChange={(e) => setEventForm(prev => ({ ...prev, artist_name: e.target.value }))}
                    placeholder="Enter artist name"
                    className="w-full px-4 py-3 border border-white/40 rounded-xl"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                    Genre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={eventForm.genre}
                    onChange={(e) => setEventForm(prev => ({ ...prev, genre: e.target.value }))}
                    placeholder="Enter genre"
                    className="w-full px-4 py-3 border border-white/40 rounded-xl"
                    required
                  />
                </div>
              </div>
              
              {/* Artist Press Kit */}
              <div>
                <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">Artist Press Kit</label>
                <div className="flex gap-2 mb-2">
                  <label className="flex items-center gap-2 px-4 py-2 border border-white/40 rounded-xl cursor-pointer hover:bg-white/60">
                    <Upload className="w-4 h-4" />
                    <span>Upload Files</span>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => handleFileUpload(e, 'artist_press_kit')}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                  <div className="flex gap-2 flex-1">
                    <input
                      type="url"
                      id="pressKitUrl"
                      placeholder="Or add URL..."
                      className="flex-1 px-4 py-2 border border-white/40 rounded-xl"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const input = document.getElementById('pressKitUrl');
                        addUrl('artist_press_kit', input.value);
                        input.value = '';
                      }}
                      className="px-4 py-2 bg-neutral-100 rounded-xl hover:bg-neutral-200"
                    >
                      <Link className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {eventForm.artist_press_kit.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {eventForm.artist_press_kit.map((file, idx) => (
                      <div key={idx} className="flex items-center gap-2 px-3 py-1 bg-neutral-100 rounded-xl text-sm">
                        <FileText className="w-4 h-4" />
                        <a href={file} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate max-w-[200px]">
                          {file.split('/').pop()}
                        </a>
                        <button type="button" onClick={() => removeFile('artist_press_kit', idx)} className="text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Spike */}
              <div>
                <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                  Spike <span className="text-red-500">*</span>
                </label>
                <select
                  value={eventForm.spike}
                  onChange={(e) => setEventForm(prev => ({ ...prev, spike: e.target.value }))}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl"
                  required
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>
              
              {/* References */}
              <div>
                <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">Reference</label>
                <div className="flex gap-2 mb-2">
                  <label className="flex items-center gap-2 px-4 py-2 border border-white/40 rounded-xl cursor-pointer hover:bg-white/60">
                    <Upload className="w-4 h-4" />
                    <span>Upload Files</span>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => handleFileUpload(e, 'references')}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                  <div className="flex gap-2 flex-1">
                      <input
                        type="url"
                        id="referenceUrl"
                        placeholder="Or add URL..."
                        className="flex-1 px-4 py-2 border border-white/40 rounded-xl"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const input = document.getElementById('referenceUrl');
                          addUrl('references', input.value);
                          input.value = '';
                        }}
                        className="px-4 py-2 bg-neutral-100 rounded-xl hover:bg-neutral-200"
                      >
                        <Link className="w-4 h-4" />
                      </button>
                    </div>
                </div>
                {eventForm.references.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {eventForm.references.map((file, idx) => (
                      <div key={idx} className="flex items-center gap-2 px-3 py-1 bg-neutral-100 rounded-xl text-sm">
                        <FileText className="w-4 h-4" />
                        <a href={file} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate max-w-[200px]">
                          {file.split('/').pop()}
                        </a>
                        <button type="button" onClick={() => removeFile('references', idx)} className="text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Notes Section */}
              <div>
                <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">Notes</label>
                <textarea
                  value={eventForm.notes || ''}
                  onChange={(e) => setEventForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Add any additional notes about this event..."
                  className="w-full px-4 py-3 border border-white/40 rounded-xl resize-none"
                  rows={3}
                />
              </div>
              
              {/* Adapts Section - Show for both new and edit */}
              <div className="border-t border-white/40 pt-4">
                <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                  Adapts <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-neutral-500 mb-3">
                  Select adapts and set due dates. 
                  <span className={eventForm.spike === 'Yes' ? 'text-orange-600 font-medium' : 'text-blue-600 font-medium'}>
                    {eventForm.spike === 'Yes' 
                      ? ' 📅 Spike Event: Default due date is 40 days before event' 
                      : ' 📅 Non-Spike Event: Default due date is 7 days before event'}
                  </span>
                </p>
                
                {/* Adapt Checklist from Admin */}
                {adapts.length > 0 && (
                  <div className="mb-4 space-y-2">
                    <p className="text-xs text-neutral-500 uppercase tracking-wide font-medium">Available Adapts</p>
                    <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                      {adapts.map(adapt => {
                        const isSelected = selectedAdapts.find(a => a.adapt_id === adapt._id || (a.platform === adapt.platform && a.size === adapt.size));
                        return (
                          <label
                            key={adapt._id}
                            className={`flex items-center gap-3 p-2 rounded-xl border cursor-pointer ${
                              isSelected ? 'border-purple-500 bg-purple-50' : 'border-white/40 hover:bg-white/60'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={!!isSelected}
                              onChange={() => toggleAdaptSelection(adapt)}
                              className="w-4 h-4 text-purple-600 rounded"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#1a1a2e]">{adapt.platform}</p>
                                <p className="text-xs text-neutral-500">{adapt.size} • {adapt.type}</p>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Custom Adapt Entry */}
                  <div className="mb-4 p-3 bg-white/40 rounded-xl border border-white/40">
                    <p className="text-xs text-neutral-500 uppercase tracking-wide font-medium mb-2">Add Custom Adapt</p>
                    <div className="grid grid-cols-5 gap-2">
                      <input
                        type="text"
                        placeholder="Platform"
                        value={customAdaptEntry.platform}
                        onChange={(e) => setCustomAdaptEntry(prev => ({ ...prev, platform: e.target.value }))}
                        className="px-3 py-2 border border-white/40 rounded-xl text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Size"
                        value={customAdaptEntry.size}
                        onChange={(e) => setCustomAdaptEntry(prev => ({ ...prev, size: e.target.value }))}
                        className="px-3 py-2 border border-white/40 rounded-xl text-sm"
                      />
                      <select
                        value={customAdaptEntry.type}
                        onChange={(e) => setCustomAdaptEntry(prev => ({ ...prev, type: e.target.value }))}
                        className="px-3 py-2 border border-white/40 rounded-xl text-sm"
                      >
                        <option value="Static">Static</option>
                        <option value="Video">Video</option>
                      </select>
                      <input
                        type="date"
                        value={customAdaptEntry.due_date}
                        onChange={(e) => setCustomAdaptEntry(prev => ({ ...prev, due_date: e.target.value }))}
                        className="px-3 py-2 border border-white/40 rounded-xl text-sm"
                      />
                      <button
                        type="button"
                        onClick={addCustomAdapt}
                        className="px-3 py-2 bg-purple-500 text-white rounded-xl text-sm hover:bg-purple-600"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Selected Adapts with Due Dates */}
                  {selectedAdapts.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs text-neutral-500 uppercase tracking-wide font-medium">Selected Adapts & Due Dates</p>
                      {selectedAdapts.map((adapt, idx) => (
                        <div key={adapt.adapt_id || adapt.temp_id} className="flex items-center gap-3 p-2 bg-purple-50 rounded-xl border border-purple-200">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-[#1a1a2e]">{adapt.platform} - {adapt.size}</p>
                            <p className="text-xs text-neutral-500">{adapt.type} {adapt.is_custom && '(Custom)'}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-neutral-500">Due:</label>
                            <input
                              type="date"
                              value={adapt.due_date || ''}
                              onChange={(e) => updateAdaptDueDate(adapt.adapt_id || adapt.temp_id, e.target.value)}
                              className="px-2 py-1 border border-white/40 rounded text-sm"
                              required
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeAdapt(adapt.adapt_id || adapt.temp_id)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              
              {/* View Tasks for existing event */}
              {selectedEvent?.tasks && selectedEvent.tasks.length > 0 && (
                <div className="border-t border-white/40 pt-4">
                  <label className="block text-sm font-semibold text-[#1a1a2e] mb-3">Associated Tasks</label>
                  <div className="space-y-2">
                    {selectedEvent.tasks.map(task => (
                      <div key={task._id} className="flex items-center justify-between p-3 bg-white/40 rounded-xl">
                        <div>
                          <p className="font-medium text-[#1a1a2e]">{task.title}</p>
                          <p className="text-sm text-neutral-500">{task.description}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          task.status === 'Completed' ? 'bg-green-100 text-green-700' :
                          task.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                          'bg-neutral-100 text-neutral-700'
                        }`}>
                          {task.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Submit Buttons */}
              <div className="flex items-center justify-between pt-4 border-t border-white/40">
                <div>
                  {selectedEvent && (userType === 'admin' || selectedEvent.can_delete !== false) && (
                    <button
                      type="button"
                      onClick={handleDeleteEvent}
                      className="px-6 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 font-medium"
                      data-testid="delete-event-btn"
                    >
                      Delete Event
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => { setShowEventModal(false); setIsEditMode(false); }}
                    className="px-6 py-3 border border-white/40 rounded-xl text-neutral-700 hover:bg-white/60 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={uploading || savingEvent}
                    className={`px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl font-medium transition-all ${
                      (uploading || savingEvent) ? 'opacity-60 cursor-not-allowed' : 'hover:opacity-90'
                    }`}
                  >
                    {savingEvent ? 'Saving...' : (uploading ? 'Uploading...' : (selectedEvent ? 'Save Changes' : 'Create Event'))}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* CSV Upload Modal */}
      {showCSVModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-xl shadow-xl max-w-lg w-full">
            <div className="border-b border-white/40 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#1a1a2e]">Upload Events from CSV</h2>
              <button onClick={() => { setShowCSVModal(false); setCsvFile(null); }} className="p-2 hover:bg-neutral-100 rounded-xl">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Instructions */}
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                <h4 className="font-semibold text-purple-900 mb-2">CSV Format Instructions</h4>
                <p className="text-sm text-purple-700 mb-2">Your CSV file should have the following columns:</p>
                <ul className="text-sm text-purple-700 list-disc list-inside space-y-1">
                  <li><strong>client_name</strong> - Exact client name (required)</li>
                  <li><strong>event_name</strong> - Name of the event (required)</li>
                  <li><strong>event_date</strong> - Date in YYYY-MM-DD format (required)</li>
                  <li><strong>event_time</strong> - Time in HH:MM format (optional)</li>
                  <li><strong>artist_name</strong> - Artist or performer name (required)</li>
                  <li><strong>genre</strong> - Music/event genre (required)</li>
                  <li><strong>spike</strong> - Yes or No (required)</li>
                  <li><strong>artist_press_kit_url</strong> - URL to press kit (optional)</li>
                  <li><strong>reference</strong> - Reference URL (optional)</li>
                  <li><strong>notes</strong> - Additional notes (optional)</li>
                </ul>
                <p className="text-xs text-purple-600 mt-2 italic">
                  Note: A default &quot;Instagram Story&quot; adapt will be auto-created for each imported event.
                </p>
              </div>
              
              {/* Download Sample */}
              <button
                onClick={handleDownloadSampleCSV}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-purple-300 rounded-xl text-purple-700 hover:bg-purple-50 transition-colors"
              >
                <Download className="w-5 h-5" />
                Download Sample CSV
              </button>
              
              {/* File Upload */}
              <div className="border-2 border-dashed border-white/40 rounded-xl p-6 text-center">
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setCsvFile(e.target.files?.[0])}
                  className="hidden"
                  id="csv-upload"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <Upload className="w-10 h-10 text-neutral-400 mx-auto mb-3" />
                  <p className="text-sm text-neutral-600">
                    {csvFile ? (
                      <span className="font-medium text-purple-600">{csvFile.name}</span>
                    ) : (
                      <>Click to select CSV file or drag and drop</>
                    )}
                  </p>
                </label>
              </div>
              
              {/* Upload Button */}
              <button
                onClick={handleCSVUpload}
                disabled={!csvFile || csvUploading}
                className="w-full px-4 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {csvUploading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    Upload & Create Events
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-[#1a1a2e] mb-4">Confirm Bulk Delete</h3>
            <p className="text-neutral-600 mb-6">
              Are you sure you want to delete <strong>{selectedEventIds.length}</strong> events? 
              This action cannot be undone. All associated tasks will also be deleted.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowBulkDeleteConfirm(false)}
                className="px-4 py-2 border border-white/40 rounded-xl text-neutral-700 hover:bg-white/60"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600"
              >
                Delete {selectedEventIds.length} Events
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Day Detail Dialog for Events */}
      <Dialog open={!!dayDetailDate} onOpenChange={(open) => !open && setDayDetailDate(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto p-0 gap-0 bg-white dark:bg-[#1a1a1a]">
          <div className="bg-purple-500 text-white px-6 py-4">
            <h2 className="text-lg font-bold">
              {dayDetailDate && dayDetailDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </h2>
            <p className="text-sm text-white/80">
              {dayDetailDate && getEventsForDate(dayDetailDate).length} event{dayDetailDate && getEventsForDate(dayDetailDate).length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="p-4 space-y-2">
            {dayDetailDate && getEventsForDate(dayDetailDate).map((event, i) => (
              <div
                key={event._id || i}
                onClick={() => { setDayDetailDate(null); handleEventClick({ stopPropagation: () => {} }, event); }}
                className="flex items-center gap-3 p-3 rounded-xl border border-white/40 dark:border-white/10 hover:bg-neutral-50 dark:hover:bg-white/5 cursor-pointer transition-colors"
                data-testid={`day-detail-event-${event._id}`}
              >
                <div className="w-1 h-10 rounded-full flex-shrink-0 bg-purple-500" />
                <Music className="w-4 h-4 flex-shrink-0 text-neutral-500" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#1a1a2e] truncate">{event.event_name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {event.artist_name && <span className="text-xs text-neutral-500">{event.artist_name}</span>}
                    {event.genre && <span className="text-xs px-1.5 py-0.5 bg-neutral-100 dark:bg-white/10 rounded-full text-neutral-600">{event.genre}</span>}
                  </div>
                </div>
                {event.spike === 'Yes' && <Zap className="w-4 h-4 text-orange-500" />}
              </div>
            ))}
            {dayDetailDate && getEventsForDate(dayDetailDate).length === 0 && (
              <div className="text-center text-neutral-500 py-8">No events for this day</div>
            )}
          </div>
          {!isClientPortal && (
            <div className="border-t border-white/40 dark:border-white/10 p-4">
              <button
                onClick={() => {
                  const date = dayDetailDate;
                  setDayDetailDate(null);
                  setSelectedDate(date);
                  setSelectedEvent(null);
                  setIsEditMode(false);
                  setEventForm({
                    client_id: '', event_name: '', event_date: formatDateToLocal(date),
                    event_time: '', artist_name: '', artist_press_kit: [],
                    artist_press_kit_url: '', genre: '', spike: 'No',
                    references: [], reference_url: '', notes: '', adapts: []
                  });
                  setSelectedClient(null); setClientSearch(''); setSelectedAdapts([]);
                  setShowEventModal(true);
                }}
                className="w-full py-2.5 rounded-xl text-white font-medium bg-purple-500 hover:bg-purple-600 transition-colors"
                data-testid="event-day-detail-create"
              >
                + Create New Event
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
